import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Combine multiple className strings with Tailwind CSS class merging
 * Handles conditional classes and removes duplicates using clsx and tailwind-merge
 * @param inputs - Class names, objects, or arrays to merge
 * @returns Merged className string with proper Tailwind precedence
 */
export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

/**
 * Format a date as relative time (e.g., "2 days ago", "1 hour ago")
 * Uses Intl.RelativeTimeFormat for locale-aware formatting
 * @param date - The date to format
 * @returns Relative time string in English
 */
export function formatRelativeTime(date: Date): string {
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    const rtf = new Intl.RelativeTimeFormat("en-US", { numeric: "auto" })

    const intervals = [
        { unit: "year" as const, seconds: 31536000 },
        { unit: "month" as const, seconds: 2592000 },
        { unit: "week" as const, seconds: 604800 },
        { unit: "day" as const, seconds: 86400 },
        { unit: "hour" as const, seconds: 3600 },
        { unit: "minute" as const, seconds: 60 },
        { unit: "second" as const, seconds: 1 },
    ]

    for (const { unit, seconds } of intervals) {
        const interval = Math.floor(diffInSeconds / seconds)
        if (interval >= 1) {
            return rtf.format(-interval, unit)
        }
    }

    return rtf.format(-0, "second")
}
